/**
 * Route questions the app asks about the path it is rendering.
 *
 * They live together because they have to agree with each other and with the
 * router: `App.tsx` sets `caseSensitive` on no route, so React Router renders
 * "/Listing/123" and "/listing/123" as the same page, and a predicate that
 * reads the path its own way decides something different about two URLs the
 * router cannot tell apart.
 */

/**
 * Whether a route path is on the listing side of the app —
 * `listing/:harid/:session?` and `listing/:harid/share/:token`.
 *
 * A prefix rather than a match of those two shapes. The paths this says yes to
 * and `App.tsx` does not — `/listing/123/foo/bar`, or `/listing/search`, which
 * redirects home — render the 404 page or leave immediately, with no chat
 * mounted for the listing branches to change. Spelling the route table out a
 * second time here to exclude them would buy a disabled share button on a page
 * nobody stays on, at the price of a copy that goes stale.
 *
 * Takes the path as the router sees it (`useLocation().pathname`), which has
 * the `basename` already removed.
 *
 * Case-insensitive, because route matching is. Read case-sensitively,
 * "/Listing/123" rendered `ListingChatbotPage` while every flag around it said
 * general assistant: the URL's `listing_filter` was auto-sent as a question
 * into a conversation that already had its listing, the loading branch in
 * `ChatWindow` took the general-assistant path, and the logo, the message
 * height, the share button and the filter panel all rendered the other side of
 * the app. Analytics then pooled the two spellings into one heatmap and one
 * recording, on the promise that they render alike — which is only true while
 * every one of those decisions comes through here.
 *
 * Every listing route has something after `listing`, so that is the boundary:
 * "/listing" alone is a `:session` value and renders the general assistant.
 */
export function isListingPath(pathname: string | undefined): boolean {
  return LISTING_PATH.test(pathname ?? "");
}

/**
 * A non-empty segment after `listing`, because `App.tsx` gives every listing
 * route one: "/listing/" itself has no `:harid`, so the generic `:session`
 * route renders it as the general assistant.
 */
const LISTING_PATH = /^\/listing\/[^/]/i;
